import React from 'react';
import { Heart, Star, Copy, Check } from 'lucide-react';
import { renderYaoLine, getWuXingColor, colorizeHtml, calculateWangXiang, calculateTransformRelation, calOriatTraState, getNaJiaWithColor, renderPillar } from '../liuyao/_helper_liuyao.jsx';

/**
 * 六爻详情组件
 */
const ExploreDetailLiuyao = ({
  processedData,
  liuyaoRef,
  handleCopy,
  copied,
  handleLike,
  handleFavorite,
  isLiked,
  isFavorited,
  likesCount,
  favoritesCount,
  interactionLoading
}) => {
  const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // GuaTable 相关常量和辅助函数
  const LINE_NAMES = ["上爻", "五爻", "四爻", "三爻", "二爻", "初爻"];

  const getLiuShenWithColor = (liuShen) => {
    if (!liuShen) return "";
    const colorMap = {
      "青龙": "#10B981",
      "朱雀": "#EF4444",
      "勾陈": "#F59E0B",
      "螣蛇": "#8B5CF6",
      "白虎": "#6B7280",
      "玄武": "#3B82F6"
    };
    const color = colorMap[liuShen] || "#6B7280";
    return `<span style="color: ${color}">${liuShen}</span>`;
  };

  const getFuShenWithColor = (fuShen) => {
    if (!fuShen) return "";
    return colorizeHtml(fuShen, getWuXingColor(fuShen, "auto"));
  };

  // 内部 GuaTable 组件
  const GuaTable = ({ originalResult, transformedResult, guaTableData, hasChangedGua }) => {
    const { originalGuaName, originalPalace, originalShiType } = originalResult;
    const { transformedGuaName, transformedPalace, transformedShiType } = transformedResult;

    return (
      <div className="overflow-x-auto w-full">
        <table className="mx-auto text-sm border-collapse whitespace-nowrap">
          <thead>
            <tr>
              <th></th>
              <th className="w-2"></th>
              <th></th>
              <th className="text-center font-normal text-sm p-1">{originalGuaName}</th>
              <th></th>
              <th></th>
              {hasChangedGua && <th className="w-2"></th>}
              {hasChangedGua && (
                <>
                  <th className="text-center font-normal text-sm p-1">{transformedGuaName}</th>
                  <th></th>
                </>
              )}
            </tr>
            <tr>
              <th></th>
              <th className="w-2"></th>
              <th></th>
              <th className="text-center font-normal p-0">
                <div className="whitespace-nowrap text-xs">
                  <span style={{ color: getWuXingColor(originalPalace) }}>{originalPalace}宫 </span>
                  <span className="text-gray-500">({originalShiType})</span>
                </div>
              </th>
              <th></th>
              <th></th>
              {hasChangedGua && <th className="w-2"></th>}
              {hasChangedGua && (
                <>
                  <th className="text-center font-normal p-0">
                    <div className="whitespace-nowrap text-xs">
                      <span style={{ color: getWuXingColor(transformedPalace) }}>{transformedPalace}宫 </span>
                      <span className="text-gray-500">({transformedShiType})</span>
                    </div>
                  </th>
                  <th></th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: LINE_NAMES.length }).map((_, displayIdx) => {
              const sourceIdx = LINE_NAMES.length - 1 - displayIdx;
              const yao = guaTableData[sourceIdx];
              if (!yao) return null;

              return (
                <tr key={yao.idx ?? sourceIdx}>
                  <td className="p-0 text-center">
                    <div className="text-gray-500 text-center text-xs" dangerouslySetInnerHTML={{ __html: getLiuShenWithColor(yao.liuShen) }} />
                  </td>
                  <td className="w-2"></td>
                  <td className="p-0 text-center">
                    <div className="flex items-center justify-center gap-0 text-xs">
                      <div dangerouslySetInnerHTML={{ __html: getFuShenWithColor(yao.fuShen?.liu_qin) }} />
                      <div dangerouslySetInnerHTML={{ __html: getFuShenWithColor(yao.fuShen?.stem_branch) }} />
                    </div>
                  </td>
                  <td className="p-0 text-center">
                    <div className="flex items-center justify-center gap-0 text-xs">
                      <div className="font-normal ml-1">{typeof yao.liuqin === "object" ? JSON.stringify(yao.liuqin) : yao.liuqin}</div>
                      <div dangerouslySetInnerHTML={{ __html: getNaJiaWithColor(yao.naJia) }} />
                      {yao.wangXiang && (
                        <div className="flex flex-col justify-center ml-0.5 text-gray-400" style={{ fontSize: '0.65em', lineHeight: '1.3' }}>
                          <div>{yao.wangXiang.day}</div>
                          <div>{yao.wangXiang.month}</div>
                        </div>
                      )}
                      <div className="flex flex-col justify-center ml-0.5 text-gray-400" style={{ fontSize: '0.65em', lineHeight: '1.3' }}>
                        <div>{yao.transformRelation}</div>
                        <div>{yao.transformState}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-0 text-center font-bold text-red-600">
                    {renderYaoLine(yao.symbol)}
                  </td>
                  <td className="p-0 text-center">
                    <div className="p-0 text-center text-gray-600 text-xs">{yao.shiYing || ""}</div>
                  </td>
                  {hasChangedGua && <td className="w-2"></td>}
                  {hasChangedGua && (
                    <>
                      <td className="p-0 text-center align-top">
                        <div className="flex items-center justify-center gap-0 text-xs">
                          <div className="font-normal ml-1">{typeof yao.transformedliuqin === "object" ? JSON.stringify(yao.transformedliuqin) : yao.transformedliuqin}</div>
                          <div dangerouslySetInnerHTML={{ __html: getNaJiaWithColor(yao.transformedNaJia) }} />
                          {yao.transformedWangXiang && (
                            <div className="flex flex-col justify-center ml-0.5 text-gray-400" style={{ fontSize: '0.65em', lineHeight: '1.3' }}>
                              <div>{yao.transformedWangXiang.day}</div>
                              <div>{yao.transformedWangXiang.month}</div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-0 text-center font-bold text-red-600 align-top">
                        {renderYaoLine(yao.transSymbol)}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <div ref={liuyaoRef} className="bg-white border rounded-xl p-4">
        <div className="flex justify-between items-center border-b pb-2 mb-3">
          <div>
            <h3 className="text-sm font-bold text-gray-900">
              {processedData.baseResult.zhanlei && processedData.baseResult.zhanlei !== "—"
                ? `${processedData.baseResult.zhanlei} - `
                : ""}
              {truncateText(processedData.baseResult.zhanti, 200)}
              {processedData.baseResult.shudi && processedData.baseResult.shudi !== "—" && (
                <span className="text-xs font-normal text-gray-500 ml-2">({processedData.baseResult.shudi})</span>
              )}
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {processedData.baseResult.datetimeDisplayStr}
            </p>
          </div>
          {(processedData.baseResult.birthYearPillar && processedData.baseResult.birthYearPillar !== "—") || (processedData.originalResult.originalMonthGuaShen && processedData.originalResult.originalMonthGuaShen !== "") ? (
            <div className="flex items-center text-xs">
              {processedData.baseResult.birthYearPillar && processedData.baseResult.birthYearPillar !== "—" && (
                <>
                  <span className="text-gray-500 mr-1">本命:</span>
                  {renderPillar(processedData.baseResult.birthYearPillar)}
                </>
              )}
              {processedData.originalResult.originalMonthGuaShen && processedData.originalResult.originalMonthGuaShen !== "" && (
                <>
                  <span className={`text-gray-500 mr-1 ${processedData.baseResult.birthYearPillar && processedData.baseResult.birthYearPillar !== "—" ? "ml-2" : ""}`}>月卦身:</span>
                  <span>{processedData.originalResult.originalMonthGuaShen}</span>
                </>
              )}
            </div>
          ) : null}
        </div>

        {/* 六爻柱子显示 */}
        <div className="mb-4 flex justify-center">
          <div className="w-auto">
            <div className="grid grid-cols-4 gap-4 text-xs text-gray-500 mb-1 text-center">
              <div className="font-semibold">年柱</div>
              <div className="font-semibold">月柱</div>
              <div className="font-semibold">日柱</div>
              <div className="font-semibold">时柱</div>
            </div>
            <div className="grid grid-cols-4 gap-4 text-center">
              <div className="flex justify-center">{renderPillar(processedData.baseResult.yearPillar)}</div>
              <div className="flex justify-center">{renderPillar(processedData.baseResult.monthPillar)}</div>
              <div className="flex justify-center">{renderPillar(processedData.baseResult.dayPillar)}</div>
              <div className="flex justify-center">{renderPillar(processedData.baseResult.hourPillar)}</div>
            </div>
            <div className="grid grid-cols-4 gap-4 text-xs text-gray-500 mt-1">
              <div></div>
              <div></div>
              <div className="text-center"><span className="text-gray-700 font-medium">{processedData.baseResult.dayPillarkongwang}（空）</span></div>
              <div></div>
            </div>
          </div>
        </div>

        <GuaTable
          originalResult={processedData.originalResult}
          transformedResult={processedData.transformedResult}
          guaTableData={processedData.guaTableData}
          hasChangedGua={processedData.hasChangedGua}
        />

      </div>
      {/* 底部操作：左侧点赞收藏，右侧复制 */}
      <div className="flex items-center justify-between mt-2 py-1">
        <div className="flex items-center gap-4">
          <button
            onClick={handleLike}
            disabled={interactionLoading}
            className={`flex items-center gap-1 transition-all ${
              isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'
            } ${interactionLoading ? 'opacity-50' : ''}`}
            title={isLiked ? '取消点赞' : '点赞'}
          >
            <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
            <span className="text-xs font-medium">{likesCount}</span>
          </button>
          <button
            onClick={handleFavorite}
            disabled={interactionLoading}
            className={`flex items-center gap-1 transition-all ${
              isFavorited ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-400'
            } ${interactionLoading ? 'opacity-50' : ''}`}
            title={isFavorited ? '取消收藏' : '收藏'}
          >
            <Star className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''}`} />
            <span className="text-xs font-medium">{favoritesCount}</span>
          </button>
        </div>

        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-indigo-600 bg-gray-50 hover:bg-indigo-50 rounded-md transition-colors border border-transparent hover:border-indigo-100"
          title="复制排盘结果为图片"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "已复制" : "复制结果"}
        </button>
      </div>
    </>
  );
};

export default ExploreDetailLiuyao;